import { ApiConfig, ApiEndpoint, RequestOptions, ApiResponse, ApiError, HttpMethod } from './types';

/**
 * API Client
 * Simple fetch-based HTTP client with TypeScript support
 */
export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;
  public endpoints: Record<string, ApiEndpoint>;

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL || '';
    this.endpoints = config.endpoints;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    this.timeout = config.timeout || 30000;
  }

  /**
   * Build full URL with query parameters
   */
  private buildURL(url: string, params?: Record<string, any>): string {
    const fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    if (!params) return fullURL;

    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');

    return queryString ? `${fullURL}?${queryString}` : fullURL;
  }

  /**
   * Make HTTP request
   */
  async request<T = any>(
    method: HttpMethod,
    url: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { params, data, headers, signal } = options;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(this.buildURL(url, params), {
        method,
        headers: {
          ...this.defaultHeaders,
          ...headers,
        },
        body: data ? JSON.stringify(data) : undefined,
        signal: signal || controller.signal,
      });

      clearTimeout(timeoutId);

      // Parse response
      let responseData: any;
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // Handle errors
      if (!response.ok) {
        const error: ApiError = {
          message: responseData?.message || response.statusText || 'Request failed',
          status: response.status,
          data: responseData,
        };
        throw error;
      }

      return {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      };
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw {
          message: 'Request timeout',
          status: 408,
        } as ApiError;
      }

      throw error as ApiError;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, options);
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, { ...options, data });
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, { ...options, data });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', url, { ...options, data });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, options);
  }

  /**
   * Get endpoint by key
   */
  getEndpoint(key: string): ApiEndpoint | undefined {
    return this.endpoints[key];
  }

  /**
   * Execute endpoint by key
   */
  async execute<T = any>(
    endpointKey: string,
    data?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const endpoint = this.getEndpoint(endpointKey);

    if (!endpoint) {
      throw {
        message: `Endpoint "${endpointKey}" not found`,
        status: 404,
      } as ApiError;
    }

    const { url, method } = endpoint;

    switch (method) {
      case 'GET':
        return this.get<T>(url, options);
      case 'POST':
        return this.post<T>(url, data, options);
      case 'PUT':
        return this.put<T>(url, data, options);
      case 'PATCH':
        return this.patch<T>(url, data, options);
      case 'DELETE':
        return this.delete<T>(url, options);
      default:
        throw {
          message: `Unsupported HTTP method: ${method}`,
          status: 405,
        } as ApiError;
    }
  }
}

/**
 * Create API client instance
 */
export function createApiClient(config: ApiConfig): ApiClient {
  return new ApiClient(config);
}
